/**
 * Wave 1 / #1861 F-03 — React Query identity partitioning helpers.
 *
 * Protected caches must not survive logout or cross-identity login on the same tab.
 * Query keys for protected data should include identity segments via protectedQueryKey.
 */
import { QueryClient } from '@tanstack/react-query'

export type QueryIdentity = {
  userId: string
  tenantId: string
}

/** Sentinel used when identity is unknown — must not be treated as a real tenant/user. */
export const UNKNOWN_IDENTITY: QueryIdentity = {
  userId: 'unknown-user',
  tenantId: 'unknown-tenant',
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        retry: 1,
      },
    },
  })
}

/**
 * Build a protected query key that includes authoritative identity segments.
 * Example: protectedQueryKey(identity, 'reports', 'list')
 * → ['id', tenantId, userId, 'reports', 'list']
 */
export function protectedQueryKey(
  identity: QueryIdentity | null | undefined,
  ...parts: Array<string | number | boolean | null | undefined>
): unknown[] {
  const id = identity && identity.userId && identity.tenantId
    ? identity
    : UNKNOWN_IDENTITY
  return ['id', id.tenantId, id.userId, ...parts]
}

/**
 * Cancel in-flight queries and clear all cached query/mutation state.
 * Call on every identity transition (logout, user switch, tenant switch).
 */
export async function clearProtectedQueryState(client: QueryClient): Promise<void> {
  await client.cancelQueries()
  client.clear()
  // Defensive: also reset mutation cache if present
  client.getMutationCache().clear()
}

/**
 * True when two identities represent different principals (including null → user).
 */
export function isIdentityTransition(
  prev: QueryIdentity | null | undefined,
  next: QueryIdentity | null | undefined,
): boolean {
  const pUser = prev?.userId ?? null
  const pTenant = prev?.tenantId ?? null
  const nUser = next?.userId ?? null
  const nTenant = next?.tenantId ?? null
  if (pUser === null && nUser === null) return false
  return pUser !== nUser || pTenant !== nTenant
}
